import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePlatformStore } from "@platform/store";
import { apiRequest } from "@core/services/api/apiRequest";
import {
  ONBOARDING_ACTION_TYPES,
  ACTION_STATUS,
  type Action,
} from "@core/features/actions";
import { getOnboardingPathFromAction } from "@core/features/onboarding";

/**
 * Login response from the backend.
 */
export type LoginResponse = {
  userData: Record<string, unknown>;
  authToken?: string;
};

/**
 * Hook that provides redirect logic after login.
 *
 * Redirect priority:
 * 1. Invitation redirect in URL search param `?redirect=/invitation/...` (takes priority over onboarding)
 * 2. Incomplete onboarding for enrolled programs
 * 3. URL search param `?redirect=/path` (non-invitation redirect, only after onboarding)
 * 4. Dashboard (default)
 *
 * Note: Pending invitations are fetched and displayed on the dashboard via the
 * PendingInvitationsListContainer, not during login redirect.
 *
 * Note: Invitation redirects bypass the onboarding gate because the invitation
 * claim flow handles its own onboarding. Non-invitation redirects still respect
 * the onboarding gate to prevent bypassing required onboarding steps.
 *
 * @example
 * ```tsx
 * const { redirect } = useLoginRedirect();
 *
 * const handleLoginSuccess = async (loginResponse: LoginResponse) => {
 *   await redirect(loginResponse);
 * };
 * ```
 */
export function useLoginRedirect() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setUser = usePlatformStore((s) => s.setUser);

  const redirect = useCallback(
    async (loginResponse: LoginResponse) => {
      // Set user first
      if (loginResponse.userData) {
        setUser(loginResponse.userData as any, loginResponse.authToken);
      }

      // 1. Check for invitation redirect in URL search param
      //    Takes priority over onboarding because the invitation claim flow
      //    handles its own onboarding steps
      const redirectParam = searchParams.get("redirect");
      if (redirectParam?.startsWith("/invitation/")) {
        navigate(redirectParam);
        return;
      }

      // 2. Check for incomplete onboarding (already enrolled, invitation already claimed)
      //    Only redirect if onboarding action is not completed and not in progress
      try {
        const actionsResponse = await apiRequest("/actions/", "GET");
        const actions: Action[] = actionsResponse?.actions ?? [];

        // Find onboarding action that needs to be started (not completed, not in progress)
        const pendingOnboarding = actions.find((a) => {
          if (!ONBOARDING_ACTION_TYPES.includes(a.action_type?.slug ?? "")) {
            return false;
          }
          const status = a.current_status?.name;
          return (
            status !== ACTION_STATUS.COMPLETED &&
            status !== ACTION_STATUS.IN_PROGRESS
          );
        });

        if (pendingOnboarding) {
          navigate(
            getOnboardingPathFromAction(
              pendingOnboarding,
              pendingOnboarding.program
            )
          );
          return;
        }
      } catch (error) {
        // If actions fetch fails, continue to next check
        console.error("Failed to fetch actions for login redirect:", error);
      }

      // 3. Check URL search param for explicit non-invitation redirect
      //    Only honored AFTER onboarding is complete to prevent bypassing
      //    e.g., /login?redirect=/dashboard/123
      if (redirectParam) {
        navigate(redirectParam);
        return;
      }

      // 4. Default to dashboard
      navigate("/dashboard");
    },
    [navigate, searchParams, setUser]
  );

  return { redirect };
}
